// client/src/components/Receipts/parts/ExpenseReportDialog.jsx
// AI-6: AI-generated expense report from bulk-selected receipts
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert,
  Stack, Paper, Chip, TextField, LinearProgress
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  AutoAwesome as AIIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { formatCurrency } from '../../../utils/helpers';

const ExpenseReportDialog = ({ open, onClose, selectedIds, receipts }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('Expense Report');
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/receipts/report', {
        ids: [...selectedIds],
        title,
        format: 'text'
      });
      setReport(res.data);
    } catch (err) {
      const msg = err.response?.data?.message || t('receipts.reportGenerating', 'Failed to generate report');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(report.reportText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    const blob = new Blob([report.reportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setReport(null);
    setError(null);
    setLoading(false);
    onClose();
  };

  const count = selectedIds?.size || 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <AIIcon color="primary" />
          <Typography variant="h6">{t('receipts.reportTitle', 'AI Expense Report')}</Typography>
          <Chip size="small" label={`${count} receipts`} />
        </Stack>
      </DialogTitle>

      <DialogContent>
        {!report && !loading && (
          <Box>
            <TextField
              fullWidth
              size="small"
              label="Report Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ mb: 3 }}
            />
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" color="text.secondary">
              AI will generate a professional expense report from your {count} selected receipts.
            </Typography>
          </Box>
        )}

        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography>{t('receipts.reportGenerating', 'AI is generating your report...')}</Typography>
            <LinearProgress sx={{ mt: 2 }} />
          </Box>
        )}

        {report && (
          <Box>
            {/* Summary chips */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              {report.summary?.totalAmount !== undefined && (
                <Chip label={`Total: ${formatCurrency(report.summary.totalAmount)}`} color="primary" />
              )}
              {report.summary?.receiptCount !== undefined && (
                <Chip label={`${report.summary.receiptCount} receipts`} variant="outlined" />
              )}
              {Object.entries(report.summary?.byMerchant || {}).slice(0, 3).map(([m, amt]) => (
                <Chip key={m} label={`${m}: ${formatCurrency(amt)}`} variant="outlined" size="small" />
              ))}
            </Stack>

            {/* Report text */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                maxHeight: 400,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
                bgcolor: 'background.default'
              }}
            >
              {report.reportText}
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t('common.close', 'Close')}</Button>
        {!report && !loading && (
          <Button
            variant="contained"
            startIcon={<AIIcon />}
            onClick={handleGenerate}
            disabled={count < 1}
          >
            Generate
          </Button>
        )}
        {report && (
          <>
            <Button startIcon={<CopyIcon />} onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
              Download
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseReportDialog;
